import { useEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { BottomTabBar } from './BottomTabBar';
import { CommandPalette } from './CommandPalette';
import { Footer } from './Footer';
import { ErrorBoundary } from '../ErrorBoundary';
import { EmailVerificationBanner } from '../shared/EmailVerificationBanner';
import { SessionExpiredBanner } from '../shared/SessionExpiredBanner';
import { NotificationBell } from '../shared/NotificationBell';
import { ServiceGate } from '../shared/ServiceGate';

/** Client area layout, sidebar on desktop, bottom tabs + drawer on mobile. */
export function AppLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen(o => !o);
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <div className="cosmic-page" style={{ minHeight: '100vh' }}>

      {/* ── Desktop sidebar (≥1024px) ── */}
      <aside className="sidebar">
        <Sidebar onOpenPalette={() => setPaletteOpen(true)} />
      </aside>

      {/* ── Mobile: top bar ── */}
      <header
        id="mobile-topbar"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 30,
          height: '56px',
          backgroundColor: 'rgba(15,6,32,0.92)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(212,175,55,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 1rem',
          paddingTop: 'env(safe-area-inset-top)',
        }}
      >
        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <img src="/logo.svg" alt="" aria-hidden="true" style={{ height: '36px', width: 'auto', display: 'block' }} />
          <span style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: '1.2rem', fontWeight: 400, color: 'var(--bg)', letterSpacing: '0.04em' }}>Professya</span>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <NotificationBell />
          <button
            className="btn btn-ghost btn-icon"
            onClick={() => setDrawerOpen(true)}
            aria-label="Abrir menu"
            aria-expanded={drawerOpen}
          >
            <Menu size={22} />
          </button>
        </div>
      </header>

      {/* ── Mobile: sidebar drawer ── */}
      {drawerOpen && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 45, backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)' }}
            onClick={() => setDrawerOpen(false)}
          />
          <aside
            style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: '260px', zIndex: 50, backgroundColor: '#0F0620', borderRight: '1px solid rgba(212,175,55,0.15)', boxShadow: '4px 0 32px rgba(0,0,0,0.7)', overflowY: 'auto' }}
            className="animate-fade-in-scale"
          >
            <button
              className="btn btn-ghost btn-icon"
              onClick={() => setDrawerOpen(false)}
              aria-label="Fechar menu"
              style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', zIndex: 1 }}
            >
              <X size={20} />
            </button>
            <Sidebar
              onClose={() => setDrawerOpen(false)}
              onOpenPalette={() => { setDrawerOpen(false); setPaletteOpen(true); }}
            />
          </aside>
        </>
      )}

      {/* ── Page content ── */}
      <main className="main-with-sidebar" style={{ minHeight: '100vh' }}>
        <EmailVerificationBanner />
        <ErrorBoundary>
          <ServiceGate>
            <Outlet />
          </ServiceGate>
        </ErrorBoundary>
        <Footer />
      </main>

      {/* Session-expired toast */}
      <SessionExpiredBanner />

      {/* ── Mobile: bottom tab bar ── */}
      <div id="bottom-nav-wrapper">
        <BottomTabBar onOpenPalette={() => setPaletteOpen(true)} />
      </div>

      {/* ── Command palette (Cmd+K) ── */}
      <CommandPalette isOpen={paletteOpen} onClose={() => setPaletteOpen(false)} />

      {/* Responsive visibility rules */}
      <style>{`
        #mobile-topbar        { display: none; }
        #bottom-nav-wrapper   { display: none; }

        @media (max-width: 1023px) {
          #mobile-topbar      { display: flex !important; }
          #bottom-nav-wrapper { display: block !important; }
          .main-with-sidebar  { margin-left: 0 !important;
                                padding-bottom: calc(64px + env(safe-area-inset-bottom)); }
          .sidebar            { display: none !important; }
        }
      `}</style>
    </div>
  );
}
